import React, { useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { AppListItem } from "Components/AppListItem";
import {
  ALLOWED_IMAGE_EXTENSIONS,
  AvatarGeneric,
} from "Components/Generic/SettingsUserForm/components/AvatarGeneric";
import SkeletonComponent from "Components/Generic/Skeleton";
import { useAppState } from "Context/StateContext";
import { format } from "date-fns-tz";
import { Languages } from "Models/languages";
import { Member } from "Models/members/member";
import { MemberStaff } from "Models/members/member.staff";
import { MemberUser } from "Models/members/member.user";
import { useTranslation } from "react-i18next";
import LanguagesService from "Services/languages.service";
import MembersService from "Services/members.service";
import { LanguagesUsage, MembersType, Status } from "Util/enums";
import { LanguageKeys, MemberKeys } from "Util/queries";

import { EMPTY_CHARACTER } from "../../../../../../../../../../../constants";
import { memberGenderOptions } from "../../../Tabs/Details";
import { MemberStaffDetails } from "./MemberStaffDetails";
import { MemberUserDetails } from "./MemberUserDetails";
import {
  AvatarWrapper,
  DetailsHeader,
  HeaderMessage,
  MemberDetailsContainer,
  MemberDetailsRow,
  MemberDetailsWrapper,
  NoAvatarComponent,
  SectionTitle,
  SectionWrapper,
  Wrapper,
} from "./styles";

interface IDetailsSectionProps {
  member: Member;
}

const NO_AVATAR_IMAGE = require("../../../../../../../../../../../assets/img/noUserFilled.svg");

export const handleMemberContent = <T,>(content: T[], key: keyof T) => {
  if (content && Array.isArray(content) && content.length) {
    return <>{content?.map((application) => application[key]).join(", ")}</>;
  }

  return EMPTY_CHARACTER;
};

export const handleContentCounter = <T,>(content?: T[]) => {
  if (typeof content === "object") return content.length;

  return 0;
};

export const DetailsSection = (props: IDetailsSectionProps) => {
  const { member } = props;
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();
  const [currentMember, setCurrentMember] = useState<MemberStaff | MemberUser>({
    status: Status.ACTIVE,
    companyId: currentUser?.companyId,
  } as MemberStaff | MemberUser);

  const { isFetching } = useQuery({
    queryKey: MemberKeys.getById(Number(member.id)),
    queryFn: async () => {
      const response = await MembersService.getById(Number(member.id), {
        include: JSON.stringify([
          {
            association: "picture",
            attributes: ["id", "mediaLink", "name"],
          },
          {
            association: "teams",
            attributes: ["id", "name"],
          },
          {
            association: "subcontractor",
          },
          {
            association: "branches",
            attributes: ["id", "name"],
          },
          {
            association: "staff",
            attributes: [
              "id",
              "email",
              "emergencyName",
              "emergencyPhone",
              "residencyStatus",
              "emergencyRelationship",
            ],
          },
          {
            association: "citizenship",
            attributes: ["id", "name"],
          },
          {
            association: "country",
            attributes: ["id", "name"],
          },
          {
            association: "location",
          },
          {
            association: "timezone",
          },
          {
            association: "user",
            attributes: ["id", "username"],
          },
          {
            association: "userCompany",
            include: [
              {
                association: "role",
              },
              {
                association: "applications",
              },
              {
                association: "manager",
              },
            ],
          },
        ]),
      });

      if (response?.data) {
        if (response.data.typeOfMember?.toLowerCase() === MembersType.STAFF.toLowerCase()) {
          const currentMember: MemberStaff = {
            ...response.data,
            emergencyEmail: response.data.staff?.email,
            emergencyName: response.data.staff?.emergencyName,
            emergencyPhone: response.data.staff?.emergencyPhone,
            residencyStatus: response.data.staff?.residencyStatus,
            emergencyRelationship: response.data.staff?.emergencyRelationship,
          } as MemberStaff;

          setCurrentMember(currentMember);
        } else {
          const currentMember: MemberUser = {
            ...response.data,
            typeOfUser: response.data.userCompany?.typeUser || "",
            username: response.data.user?.username || "",
            code: response.data.userCompany?.code,
            applications: response.data.userCompany?.applications,
            role: response.data.userCompany?.role,
            roleId: Number(response.data.userCompany?.roleId),
            manager: response.data.userCompany?.manager,
            leader: response.data.userCompany?.leader || false,
          } as MemberUser;

          setCurrentMember(currentMember);
        }
      }

      return response.data;
    },
    enabled: !!member.id,
    refetchOnWindowFocus: false,
  });

  const { data: translateList, isFetching: fetchingTranslateList } = useQuery({
    queryKey: LanguageKeys.getAll(["members-view"]),
    queryFn: async () => {
      const response = await LanguagesService.getAll({
        companyId: currentUser?.companyId,
        usage: LanguagesUsage.WEB,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
      });

      if (response?.data && Array.isArray(response.data)) return response.data as Languages[];

      return [];
    },
    refetchOnWindowFocus: false,
  });

  const genderOptionLabel = useMemo(
    () =>
      memberGenderOptions.find((memberGender) => memberGender.value === currentMember.gender)
        ?.label,
    [memberGenderOptions, currentMember]
  );

  function findMemberLanguageById(memberLanguageId?: number) {
    const memberLanguage = translateList?.find((translate) => translate.id === memberLanguageId);

    if (memberLanguage) return memberLanguage.name;

    return EMPTY_CHARACTER;
  }

  const renderMemberDetails = () => {
    switch (member?.typeOfMember?.toLowerCase()) {
      case MembersType.USER?.toLowerCase():
        return <MemberUserDetails currentMember={currentMember as MemberUser} />;
      case MembersType.STAFF?.toLowerCase():
        return <MemberStaffDetails currentMember={currentMember as MemberStaff} />;
      default:
        return <></>;
    }
  };

  return (
    <>
      <DetailsHeader>
        <SkeletonComponent isLoading={isFetching}>
          <div>
            <HeaderMessage hasSubcontractor={!!currentMember?.subcontractorId}>
              {translate("thisMemberIsA")}{" "}
              <span>
                {currentMember?.subcontractor
                  ? `${translate("subcontractorFor")} ${currentMember?.subcontractor?.businessName}`
                  : translate("own")}
              </span>{" "}
              {translate("andIsDefinedAsA")} {"'"}
              <b>{currentMember?.typeOfMember?.toLowerCase()}</b>
              {"'"}
            </HeaderMessage>
          </div>
        </SkeletonComponent>
      </DetailsHeader>

      <Wrapper>
        <SectionWrapper>
          <SkeletonComponent isLoading={isFetching}>
            <>
              <div>
                <SectionTitle>{translate("personalDetails")}</SectionTitle>
              </div>
              <MemberDetailsContainer>
                <AvatarWrapper>
                  {currentMember?.picture?.mediaLink ? (
                    <AvatarGeneric
                      allowedImageExtensions={ALLOWED_IMAGE_EXTENSIONS}
                      name="picture"
                      data-cy="avatar"
                      picture={currentMember?.picture?.mediaLink || ""}
                      onChangePicture={() => {}}
                      isOnViewMode
                    />
                  ) : (
                    <NoAvatarComponent>
                      <img src={NO_AVATAR_IMAGE} alt="member-has-no-avatar-image" />
                      <span>{translate("noPhotoAdded")}</span>
                    </NoAvatarComponent>
                  )}
                </AvatarWrapper>
                <MemberDetailsWrapper>
                  <MemberDetailsRow>
                    <AppListItem
                      title={translate("nickname")}
                      subtitle={currentMember.nickname}
                      verifyIfIsEmpty
                      disableGutters
                    />
                    <AppListItem
                      title={translate("firstName")}
                      subtitle={currentMember.firstName}
                      verifyIfIsEmpty
                      disableGutters
                    />
                    <AppListItem
                      title={translate("lastName")}
                      subtitle={currentMember.lastName}
                      verifyIfIsEmpty
                      disableGutters
                    />
                    <AppListItem
                      title={translate("status")}
                      subtitle={currentMember.status}
                      verifyIfIsEmpty
                      disableGutters
                    />
                  </MemberDetailsRow>
                  <MemberDetailsRow>
                    <AppListItem
                      title={translate("email")}
                      subtitle={currentMember.email}
                      verifyIfIsEmpty
                      disableGutters
                    />
                    <AppListItem
                      title={translate("mobile")}
                      subtitle={currentMember.mobile}
                      verifyIfIsEmpty
                      disableGutters
                    />
                    <AppListItem
                      title={translate("phone")}
                      subtitle={currentMember.phone}
                      verifyIfIsEmpty
                      disableGutters
                    />
                  </MemberDetailsRow>
                  <MemberDetailsRow>
                    <AppListItem
                      title={translate("dateOfBirth")}
                      subtitle={
                        currentMember.dateOfBirth
                          ? format(new Date(currentMember.dateOfBirth), "dd/MM/yyyy")
                          : EMPTY_CHARACTER
                      }
                      verifyIfIsEmpty
                      disableGutters
                    />
                    <AppListItem
                      title={translate("startDate")}
                      subtitle={
                        currentMember.startEmploymentDate
                          ? format(new Date(currentMember.startEmploymentDate), "dd/MM/yyyy")
                          : EMPTY_CHARACTER
                      }
                      verifyIfIsEmpty
                      disableGutters
                    />
                    <AppListItem
                      title={translate("endDate")}
                      subtitle={
                        currentMember.endEmploymentDate
                          ? format(new Date(currentMember.endEmploymentDate), "dd/MM/yyyy")
                          : EMPTY_CHARACTER
                      }
                      verifyIfIsEmpty
                      disableGutters
                    />
                  </MemberDetailsRow>
                  <MemberDetailsRow>
                    <AppListItem
                      title={translate("language")}
                      subtitle={
                        currentMember.languageId
                          ? findMemberLanguageById(currentMember.languageId)
                          : EMPTY_CHARACTER
                      }
                      verifyIfIsEmpty
                      disableGutters
                      isLoadingSubtitle={fetchingTranslateList}
                    />
                    <AppListItem
                      title={translate("country")}
                      subtitle={currentMember.country?.name}
                      verifyIfIsEmpty
                      disableGutters
                    />
                    <AppListItem
                      title={translate("citizenship")}
                      subtitle={currentMember.citizenship?.name}
                      verifyIfIsEmpty
                      disableGutters
                    />
                  </MemberDetailsRow>
                  <MemberDetailsRow>
                    <AppListItem
                      title={translate("license")}
                      subtitle={currentMember.license}
                      verifyIfIsEmpty
                      disableGutters
                    />
                    <AppListItem
                      title={translate("jobTitle")}
                      subtitle={currentMember.jobTitle}
                      verifyIfIsEmpty
                      disableGutters
                    />
                    <AppListItem
                      title={translate("gender")}
                      subtitle={genderOptionLabel ? translate(genderOptionLabel) : EMPTY_CHARACTER}
                      verifyIfIsEmpty
                      disableGutters
                    />
                  </MemberDetailsRow>
                </MemberDetailsWrapper>
              </MemberDetailsContainer>

              {renderMemberDetails()}
              <div>
                <SectionTitle>{translate("additional")}</SectionTitle>
              </div>
              <MemberDetailsWrapper>
                <MemberDetailsRow>
                  <AppListItem
                    title={translate("address")}
                    subtitle={currentMember.location?.longAddress}
                    verifyIfIsEmpty
                    disableGutters
                  />
                </MemberDetailsRow>
                <MemberDetailsRow>
                  <AppListItem
                    title={translate("timezone")}
                    subtitle={currentMember.timezone?.name}
                    verifyIfIsEmpty
                    disableGutters
                  />
                  <AppListItem
                    title={translate("branch")}
                    renderCounter
                    contentCountValue={handleContentCounter(currentMember.branches)}
                    subtitle={handleMemberContent(currentMember.branches || [], "name")}
                    verifyIfIsEmpty
                    disableGutters
                  />
                  <AppListItem
                    title={translate("teams")}
                    renderCounter
                    contentCountValue={handleContentCounter(currentMember.teams)}
                    subtitle={handleMemberContent(currentMember.teams || [], "name")}
                    verifyIfIsEmpty
                    disableGutters
                  />
                </MemberDetailsRow>
              </MemberDetailsWrapper>
            </>
          </SkeletonComponent>
        </SectionWrapper>
      </Wrapper>
    </>
  );
};
